import React, { useState, useEffect, useCallback } from 'react';
import AfterQuestion from './AfterQuestion';
import QuizQuestion from './QuizQuestion';
import { useTranslation } from 'react-i18next';
import ProgressBar from '../ProgressBar';
import { getSelectedCompanies } from '../../data/companies';
import { convertVimeoUrlsToEmbeds } from '../../helpers/htmlUtils';

/**
 * Quiz
 *
 * @param {object} questions - The quiz data
 * @param {function} submitQuiz - Submit the quiz
 * @param {boolean} quizFinished - Whether the quiz has already been finished before
 * @param {object} errors - Contains error messages when there are errors
 *
 * @return {JSX.Element} Quiz component
 */
export default function Quiz({
  questions,
  submitQuiz,
  noAnswers,
  quizFinished,
  quizExpired,
  notAllQuestionsAnswered,
  errors,
  correctAnswers,
  confirmationData,
  isSubmitting,
}) {
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [feedbackResponses, setFeedbackResponses] = useState([]);
  const [questionShown, setQuestionShown] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(
    questions ? questions.length : 0
  );
  const [questionsWithAnswersCount, setQuestionsWithAnswersCount] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [showCompanySelection, setShowCompanySelection] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [companyOther, setCompanyOther] = useState('');
  const [companyError, setCompanyError] = useState(false);
  const [feedbackError, setFeedbackError] = useState(false);
  const { t } = useTranslation('quiz');
  const companies = getSelectedCompanies();

  /**
   * @param {number} index - The answer index
   * @param {number} id - The question id
   * @param {number} correctAnswer - The correct answer index
   */
  const changeAnswer = useCallback((index, id, correctAnswer) => {
    setSelectedAnswers((prevAnswers) => {
      const answersIndex = prevAnswers.findIndex((obj) => obj.question === id);

      // If already answered with the same value, don't update state
      if (answersIndex >= 0 && prevAnswers[answersIndex].answer === index) {
        return prevAnswers;
      }

      const answers = [...prevAnswers];
      if (answersIndex < 0) {
        answers.push({
          question: id,
          answer: index,
          correctAnswerIndex: correctAnswer,
        });
      } else {
        answers[answersIndex].answer = index;
        answers[answersIndex].correctAnswerIndex = correctAnswer;
      }

      return answers;
    });
  }, []);

  /**
   * @param {number} id - The question id
   * @param {string} response - The feedback text
   * @param {string} questionText - The question text
   */
  const changeFeedback = useCallback((id, response, questionText) => {
    setFeedbackResponses((prevFeedbacks) => {
      const feedbackIndex = prevFeedbacks.findIndex((obj) => obj.question === id);

      const feedbacks = [...prevFeedbacks];
      if (feedbackIndex < 0) {
        feedbacks.push({
          question: id,
          response: response,
          questionText: questionText,
        });
      } else {
        feedbacks[feedbackIndex].response = response;
        feedbacks[feedbackIndex].questionText = questionText;
      }

      return feedbacks;
    });
    
    // Clear feedback error when user provides feedback
    if (response.trim()) {
      setFeedbackError(false);
    }
  }, []);

  /**
   * Redirect to the previous question.
   */
  function previousQuestion() {
    if (showCompanySelection) {
      setShowCompanySelection(false);
      setShowSubmit(true);
      setCompanyError(false);
      window.scrollTo(0, 0);
    } else {
      setQuestionShown((prevState) => prevState - 1);
      setShowAnswer(true);
      setShowSubmit(false);
    }
  }

  useEffect(() => {
    if (questions) {
      setTotalQuestions(questions.length);
      
      // Count only questions that have actual answer options AND have a distinct correct answer
      // Exclude questions where all answers have the same points ("all correct" questions)
      const questionsWithAnswers = questions.filter(q => {
        const validAnswers = q?.answers?.filter(a => a?.text && a.text.trim() !== '') || [];
        if (validAnswers.length === 0) return false;
        
        // Exclude questions where all answers have the same points
        const allAnswersSamePoints = validAnswers.every(a => a.points === validAnswers[0]?.points);
        return !allAnswersSamePoints;
      });
      setQuestionsWithAnswersCount(questionsWithAnswers.length);
    }
  }, [questions]);

  return (
    <div className="quiz">
      <div className="container">
        <div className="row">
          {quizFinished && (
            // Will be shown directly after submit
            <>
              <div className="col-12 col-lg-5 offset-lg-1">
                <div className="page-content">
                  <div
                    className="page-content__wrapper"
                    dangerouslySetInnerHTML={{
                      __html: convertVimeoUrlsToEmbeds(confirmationData?.confirmation),
                    }}
                  />
                  {confirmationData.button_url &&
                    confirmationData.button_title && (
                      <a
                        href={confirmationData.button_url}
                        alt={confirmationData.button_title}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="button button--pagination confirmation"
                      >
                        {confirmationData.button_title}
                      </a>
                    )}
                </div>
              </div>
              <div className="col-12 col-lg-5">
                <div className="score">
                  <h3>{t('score')}</h3>
                  <h3>{correctAnswers}</h3>
                  <hr className="score__border" />
                  <h3>{questionsWithAnswersCount}</h3>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {questions.length && !quizFinished && !showCompanySelection && (
        <div key={questions[questionShown]?.id}>
          {questions[questionShown]?.content && (
            <AfterQuestion
              afterQuestionContent={questions[questionShown]?.content}
              image={questions[questionShown]?.after_question_image}
              previousQuestion={previousQuestion}
              questionShown={questionShown}
            />
          )}
          <div className="container" id="question-block">
            <div className="row">
              <div className="col-12 col-md-10 offset-md-1">
                <div className="quiz__wrapper">
                  <div className="quiz__wrapper--content">
                    <ProgressBar
                      currentQuestion={questionShown}
                      questions={questions}
                    />
                    <QuizQuestion
                      questionData={questions[questionShown]}
                      changeAnswer={changeAnswer}
                      changeFeedback={changeFeedback}
                      quizFinished={quizFinished}
                      quizExpired={quizExpired}
                      selectedAnswers={selectedAnswers}
                      feedbackResponses={feedbackResponses}
                      showAnswer={showAnswer}
                    />
                    {feedbackError && (
                      <div className="col-12">
                        <p className="feedback-error">
                          {t('feedbackIncomplete')}
                        </p>
                      </div>
                    )}
                    <div className="button-wrapper">
                      {questionShown !== 0 && (
                        <button
                          className="button previous"
                          onClick={() => {
                            previousQuestion();
                            setShowAnswer(true);
                            window.scrollTo(0, 0);
                          }}
                        >
                          {t('previous')}
                        </button>
                      )}
                      {!showSubmit ? (
                        <button
                          className={`button next ${
                            (() => {
                              const currentQuestion = questions[questionShown];
                              const hasValidAnswers = currentQuestion?.answers?.filter(a => a?.text && a.text.trim() !== '').length > 0;
                              // Don't hide if current question has no answers OR if we've answered this question
                              const currentQuestionAnswered = selectedAnswers.some(
                                (ans) => ans.question === currentQuestion?.id
                              );
                              return !hasValidAnswers || currentQuestionAnswered ? '' : 'hide-button';
                            })()
                          }`}
                          disabled={(() => {
                            const currentQuestion = questions[questionShown];
                            if (currentQuestion?.type === 'feedback' && currentQuestion?.feedback_required) {
                              const feedbackIndex = feedbackResponses.findIndex(f => f.question === currentQuestion.id);
                              return feedbackIndex < 0 || !feedbackResponses[feedbackIndex]?.response?.trim();
                            }
                            return false;
                          })()}
                          onClick={() => {
                            const currentQuestion = questions[questionShown];
                            const questionType = currentQuestion?.type || 'single-choice';
                            const hasValidAnswers = currentQuestion?.answers?.filter(a => a?.text && a.text.trim() !== '').length > 0;
                            
                            // Helper function to validate feedback and go to company selection
                            const goToCompanySelection = () => {
                              const requiredFeedbackQuestions = questions.filter(q => q.type === 'feedback' && q.feedback_required);
                              const missingFeedback = requiredFeedbackQuestions.some(q => {
                                const feedbackIndex = feedbackResponses.findIndex(f => f.question === q.id);
                                return feedbackIndex < 0 || !feedbackResponses[feedbackIndex]?.response?.trim();
                              });
                              
                              if (missingFeedback) {
                                setFeedbackError(true);
                              } else {
                                setFeedbackError(false);
                                setShowCompanySelection(true);
                                window.scrollTo(0, 0);
                              }
                            };
                            
                            if (showAnswer || !hasValidAnswers || questionType === 'feedback') {
                              // Don't go past the last question
                              if (questionShown + 1 < totalQuestions) {
                                const nextQuestionIndex = questionShown + 1;
                                const nextQuestion = questions[nextQuestionIndex];
                                
                                setQuestionShown(nextQuestionIndex);

                                // If the next question was already answered, show the answer.
                                // Check specifically if next question has an answer in selectedAnswers
                                const nextQuestionAnswered = selectedAnswers.some(
                                  (ans) => ans.question === nextQuestion?.id
                                );
                                const nextQuestionHasAnswers = nextQuestion?.answers?.filter(
                                  (a) => a?.text && a.text.trim() !== ''
                                ).length > 0;
                                
                                if (nextQuestionAnswered && nextQuestionHasAnswers) {
                                  setShowAnswer(true);
                                } else {
                                  setShowAnswer(false);
                                }
                                window.scrollTo(0, 0);
                              } else if (questionShown + 1 === totalQuestions) {
                                // Last question - go directly to company selection
                                goToCompanySelection();
                              }
                            } else {
                              setShowAnswer(true);

                              // Show the submit button if all questions with answers are answered AND we're on the last question
                              // Use >= to account for auto-marked info questions that may be in selectedAnswers
                              if (selectedAnswers.length >= questionsWithAnswersCount && questionShown + 1 === totalQuestions) {
                                // Validate required feedback before showing submit
                                const requiredFeedbackQuestions = questions.filter(q => q.type === 'feedback' && q.feedback_required);
                                const missingFeedback = requiredFeedbackQuestions.some(q => {
                                  const feedbackIndex = feedbackResponses.findIndex(f => f.question === q.id);
                                  return feedbackIndex < 0 || !feedbackResponses[feedbackIndex]?.response?.trim();
                                });
                                
                                if (missingFeedback) {
                                  setFeedbackError(true);
                                } else {
                                  setFeedbackError(false);
                                  setShowSubmit(true);
                                }
                              }
                            }
                          }}
                        >
                          {showAnswer || !questions[questionShown]?.answers?.filter(a => a?.text && a.text.trim() !== '').length ? (
                            <>{t('next')}</>
                          ) : (
                            <>{t('submitAnswer')}</>
                          )}
                        </button>
                      ) : (
                        <button
                          className="button next"
                          onClick={() => {
                            setShowCompanySelection(true);
                            window.scrollTo(0, 0);
                          }}
                        >
                          {t('next')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCompanySelection && !quizFinished && (
        <div className="container" id="company-selection-block">
          <div className="row">
            <div className="col-12 col-md-10 offset-md-1">
              <div className="quiz__wrapper">
                <div className="quiz__wrapper--content">
                  <h3 className="quiz__title">{t('company')}</h3>
                  <p className="quiz__description">{t('company_description')}</p>
                  <div className="company-selection">
                    <select
                      id="company-select"
                      value={selectedCompany}
                      onChange={(e) => {
                        setSelectedCompany(e.target.value);
                        setCompanyError(false);
                        if (e.target.value !== 'other') {
                          setCompanyOther('');
                        }
                      }}
                      className={companyError ? 'error' : ''}
                    >
                      <option value="">{t('placeholder_company')}</option>
                      {Object.entries(companies).map(([key, value]) => (
                        <option key={key} value={key}>
                          {value}
                        </option>
                      ))}
                    </select>
                    {selectedCompany === 'other' && (
                      <input
                        type="text"
                        value={companyOther}
                        onChange={(e) => {
                          setCompanyOther(e.target.value);
                          setCompanyError(false);
                        }}
                        placeholder={t('company_other_placeholder')}
                        className={companyError && !companyOther ? 'error' : ''}
                      />
                    )}
                    {companyError && (
                      <p className="error-message">
                        {selectedCompany === 'other' ? t('company_other_required') : t('company_required')}
                      </p>
                    )}
                  </div>
                  <div className="button-wrapper">
                    <button
                      className="button previous"
                      onClick={() => {
                        previousQuestion();
                      }}
                    >
                      {t('previous')}
                    </button>
                    <button
                      className="button next"
                      onClick={() => {
                        // Validate company selection
                        if (!selectedCompany || (selectedCompany === 'other' && !companyOther.trim())) {
                          setCompanyError(true);
                          return;
                        }
                        
                        // Validate required feedback questions
                        const requiredFeedbackQuestions = questions.filter(q => q.type === 'feedback' && q.feedback_required);
                        const missingFeedback = requiredFeedbackQuestions.some(q => {
                          const feedbackIndex = feedbackResponses.findIndex(f => f.question === q.id);
                          return feedbackIndex < 0 || !feedbackResponses[feedbackIndex]?.response?.trim();
                        });
                        
                        if (missingFeedback) {
                          alert(t('feedbackIncomplete'));
                          return;
                        }
                        
                        const companyData = {
                          company: selectedCompany === 'other' ? companyOther : companies[selectedCompany],
                          companyKey: selectedCompany
                        };
                        
                        submitQuiz(selectedAnswers, feedbackResponses, companyData);
                      }}
                      disabled={quizFinished || isSubmitting}
                    >
                      {isSubmitting ? (
                        <span className="button-spinner"></span>
                      ) : (
                        t('submitQuiz')
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
